import { useUserClassificationSelector } from '../extra/store'
import { TypeFilter } from '../../../common/TypeFilter/TypeFilter'
import { useCallback } from 'react'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'

/**
 * @param {Record<string, unknown>} fieldErrors
 * @returns {boolean}
 */
const hasRecordFieldErrors = (fieldErrors) => {
  if (!fieldErrors || typeof fieldErrors !== 'object') return false
  return Object.values(fieldErrors).some((v) => Array.isArray(v) && v.length > 0)
}

export const UserClassificationType = (props) => {
  const { className, showModal, onSelectClassificationType } = props
  const classificationTypes = useUserClassificationSelector((s) => s.userClassificationTypes)
  const selectedClassificationType = useUserClassificationSelector((s) => s.selectedUserClassificationType)
  const userClassificationRecords = useUserClassificationSelector((s) => s.userClassificationRecords)
  const updateStatus = useUserClassificationSelector((s) => s.updateStatus)
  const errors = useUserClassificationSelector((s) => s.errors)

  // userClassificationRecordsの実際の件数を使用する関数
  // 区分項目の追加/削除時に自動更新される
  // 初期表示時はIF02078のレスポンスのitemCountを使用
  // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を返す
  const recordCountGetter = useCallback(
    (code) => {
      const records = userClassificationRecords[code] || []
      // 区分項目が取得済みの場合は実際の件数を使用
      if (records.length > 0) {
        // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を返す
        return records.length
      }
      // 区分項目が未取得の場合は、IF02078のレスポンスのitemCountを使用
      const type = classificationTypes.find((t) => t.code === code)
      return type?.itemCount ?? 0
    },
    [userClassificationRecords, classificationTypes]
  )

  // モーダルの幅を広げるためのフック
  const { promisify: promisifyModalWidth } = useModifyClassList('.modal-window', ['w-[600px]'])

  /** @function openMemoModal 区分の説明モーダルを表示 */
  const openMemoModal = useCallback(
    async (type) => {
      if (!showModal) return

      try {
        await promisifyModalWidth((res) => {
          showModal({
            title: type.label || '区分の説明',
            isWide: false,
            isFooter: false,
            onClose: res,
            titleAlign: 'left',
            closeButtonStyle: 'icon',
            body: (
              <div className="py-4">
                <p className="text-base text-gray-700 whitespace-pre-wrap">{type.memo || '説明が設定されていません'}</p>
              </div>
            )
          })
        })
      } catch (error) {
        // モーダル表示エラーはユーザーに影響しないため、ログのみ記録
        console.error('区分説明モーダル表示エラー:', error)
      }
    },
    [showModal, promisifyModalWidth]
  )

  // ステータス取得関数
  const statusGetter = useCallback(
    (code) => {
      return updateStatus[code] || null
    },
    [updateStatus]
  )

  const itemErrorGetter = useCallback(
    (code) => {
      const byClass = errors?.byClassification?.[code]
      if (Array.isArray(byClass) && byClass.length > 0) return true
      const importVal = errors?.importValidationErrors?.[code]
      if (Array.isArray(importVal) && importVal.length > 0) return true
      if (updateStatus?.[code] === 'error') return true
      const rve = errors?.recordValidationErrors || {}
      const recs = userClassificationRecords[code] || []
      if (recs.some((r) => hasRecordFieldErrors(rve[r.key]))) return true
      return false
    },
    [errors, updateStatus, userClassificationRecords]
  )

  return (
    <TypeFilter
      title="区分"
      types={classificationTypes}
      onSelectItem={onSelectClassificationType}
      selectedItemCode={selectedClassificationType}
      recordCountGetter={recordCountGetter}
      statusGetter={statusGetter}
      itemErrorGetter={itemErrorGetter}
      onShowMemoModal={openMemoModal}
    />
  )
}
